// Procedural sounds for the gaps in the Godot bank: computed once into AudioBuffers (plain
// math, no OfflineAudioContext), loops crossfaded end-to-start so they're seamless.

type Gen = (sr: number) => Float32Array;

function rng(seed: number) {
  let s = seed >>> 0 || 1;
  return () => { s ^= s << 13; s ^= s >>> 17; s ^= s << 5; return ((s >>> 0) / 4294967296) * 2 - 1; };
}

/** Make a loop of `len` samples seamless from a buffer of len + xf samples. */
function seamless(buf: Float32Array, len: number, xf: number) {
  const out = new Float32Array(len);
  for (let i = 0; i < len; i++) out[i] = buf[i];
  for (let i = 0; i < xf; i++) { const t = i / xf; out[i] = buf[i] * t + buf[len + i] * (1 - t); }
  return out;
}

function normalize(b: Float32Array, peak: number) {
  let m = 0;
  for (let i = 0; i < b.length; i++) m = Math.max(m, Math.abs(b[i]));
  if (m > 0) for (let i = 0; i < b.length; i++) b[i] *= peak / m;
  return b;
}

const toasterPop: Gen = (sr) => {
  const n = Math.floor(sr * 0.42), b = new Float32Array(n), r = rng(7);
  let ph = 0, ph2 = 0, hp = 0, lp = 0;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    // spring boing: rising glide with a wobble
    const f = 420 + 520 * (1 - Math.exp(-t * 14)) + 60 * Math.sin(t * 2 * Math.PI * 28) * Math.exp(-t * 8);
    ph += (2 * Math.PI * f) / sr;
    const boing = Math.sin(ph) * Math.exp(-t * 7) * Math.min(1, t * 400);
    // tin ping
    ph2 += (2 * Math.PI * 2350) / sr;
    const ping = Math.sin(ph2) * Math.exp(-t * 30) * 0.35;
    // the lever click
    const x = r();
    lp += 0.3 * (x - lp); hp = x - lp;
    const click = hp * Math.exp(-t * 400) * 1.2;
    b[i] = boing * 0.7 + ping + click;
  }
  return normalize(b, 0.8);
};

const land: Gen = (sr) => {
  const n = Math.floor(sr * 0.3), b = new Float32Array(n), r = rng(11);
  let ph = 0, lp = 0;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    const f = 95 * Math.exp(-t * 9) + 42;
    ph += (2 * Math.PI * f) / sr;
    lp += 0.08 * (r() - lp);
    b[i] = Math.sin(ph) * Math.exp(-t * 16) + lp * 2.2 * Math.exp(-t * 22);
  }
  return normalize(b, 0.8);
};

const jetpack: Gen = (sr) => {
  const len = Math.floor(sr * 2), xf = Math.floor(sr * 0.25), n = len + xf;
  const b = new Float32Array(n), r = rng(23);
  let lp1 = 0, lp2 = 0, low = 0, band = 0, ph = 0, wob = 0;
  const f = 2 * Math.sin((Math.PI * 1900) / sr);
  for (let i = 0; i < n; i++) {
    const x = r();
    lp1 += 0.09 * (x - lp1);
    lp2 += 0.09 * (lp1 - lp2); // roar
    low += f * band; const high = x - low - 0.7 * band; band += f * high; // hiss
    wob += 0.0005 * (r() - wob);
    ph += (2 * Math.PI * (52 + wob * 400)) / sr;
    b[i] = lp2 * 3.2 + band * 0.18 + Math.sin(ph) * 0.25 * (0.7 + 0.3 * lp1 * 4);
  }
  return normalize(seamless(b, len, xf), 0.7);
};

const wind: Gen = (sr) => {
  const len = Math.floor(sr * 3), xf = Math.floor(sr * 0.4), n = len + xf;
  const b = new Float32Array(n), r = rng(31);
  let low = 0, band = 0, lp = 0;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    const fc = 520 + 380 * Math.sin((2 * Math.PI * t) / 3) + 120 * Math.sin((2 * Math.PI * t * 2) / 3 + 1);
    const f = 2 * Math.sin((Math.PI * fc) / sr);
    const x = r();
    low += f * band; const high = x - low - 1.1 * band; band += f * high;
    lp += 0.02 * (x - lp);
    b[i] = band * 0.6 + lp * 1.5;
  }
  return normalize(seamless(b, len, xf), 0.7);
};

const laser: Gen = (sr) => {
  const len = sr, b = new Float32Array(len), r = rng(5);
  let lp = 0;
  for (let i = 0; i < len; i++) {
    const t = i / sr;
    // whole cycles per loop: 1760 Hz, 2640 Hz, 8 Hz tremolo
    const trem = 0.75 + 0.25 * Math.sin(2 * Math.PI * 8 * t);
    lp += 0.5 * (r() - lp);
    b[i] = (Math.sin(2 * Math.PI * 1760 * t) * 0.6 + Math.sin(2 * Math.PI * 2640 * t) * 0.2 + Math.sin(2 * Math.PI * 110 * t) * 0.25) * trem + lp * 0.04;
  }
  return normalize(b, 0.5);
};

export const SYNTH: Record<string, Gen> = {
  toaster_pop: toasterPop,
  land,
  jetpack,
  flight_wind: wind,
  sniper_laser: laser,
};

export function synthBuffer(ctx: BaseAudioContext, name: string): AudioBuffer | null {
  const g = SYNTH[name];
  if (!g) return null;
  const data = g(ctx.sampleRate);
  const buf = ctx.createBuffer(1, data.length, ctx.sampleRate);
  buf.getChannelData(0).set(data);
  return buf;
}
